import { useState } from 'react'
import {
  HeaderChildrenContainer,
  Wrapper
} from './components/display/commonStyledComponents'
import { Button, Check, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { generalConstants } from 'locale/general'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useParams } from 'react-router-dom'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { Spinner } from 'components/generic'
import useIncidentReport from 'hooks/useIncidentReport/useIncidentReport'
import IncidentCaseForm, {
  IncidentReportCreateCaseForm
} from './components/forms/IncidentCaseForm'
import { getNewFilesFormData } from './components/UploadAttachmentsOnSaveInput'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import {
  prepareCreateInitialValues,
  prepareCreateBody
} from './utils/incidentCaseFormUtils'

type IncidentReportCreateCaseScreenProps = {
  testId?: string
}

const IncidentReportCreateCaseScreen = ({
  testId = 'incident-create-case'
}: IncidentReportCreateCaseScreenProps = {}) => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()

  const { incidentReport, isIncidentReportLoading, refetch } =
    useIncidentReportByLog({
      incidentLogId
    })

  const { createIncidentReportCase, uploadIncidentReportFiles } =
    useIncidentReport({
      incidentReportId: incidentReport?.id,
      incidentLogId,
      onUpdateSuccess: () => {
        showToast({
          title: t(incidentReportsConstants.CASE_UPDATED)
        })
      },
      onUploadFilesSuccess: refetch
    })

  if (isIncidentReportLoading)
    return <Spinner data-testid={`${testId}-spinner`} />
  if (!incidentReport)
    return (
      <EmptyState
        data-testid={`${testId}-empty-state`}
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues = prepareCreateInitialValues(incidentReport)

  const onFinish = async (values: IncidentReportCreateCaseForm<string>) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)

      const incidentReportCreateCaseBody = prepareCreateBody(values)
      await createIncidentReportCase(incidentReportCreateCaseBody)

      const formData = getNewFilesFormData(values.attachments)
      if (formData) {
        await uploadIncidentReportFiles(formData)
      }

      navigate(`../`, { relative: 'path' })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error'
      })
    } finally {
      setIsUpdateLoading(false)
    }
  }

  const onValuesChange = (values: any) => {
    if (values) {
      setIsDirty(true)
    }
  }

  return (
    <>
      <PageHeader
        testId={testId}
        sticky
        title={t(incidentReportsConstants.CREATING_INCIDENT_CASE)}
        onBack={() => navigate('../', { relative: 'path' })}
      >
        <HeaderChildrenContainer data-testid={`${testId}-header-actions`}>
          <Button
            data-testid={`${testId}-cancel-button`}
            onClick={() => navigate('../', { relative: 'path' })}
          >
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            data-testid={`${testId}-save-button`}
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper data-testid={`${testId}-wrapper`}>
        <IncidentCaseForm
          testId={`${testId}-case-form`}
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          incidentReport={incidentReport}
        />
        <NavigationModal canNavigate={!isDirty} />
      </Wrapper>
    </>
  )
}

export default IncidentReportCreateCaseScreen
